import React, { useContext, useState, useEffect } from 'react';
import { MitWebsocket } from 'src/common/managers/MitWebsocket';
import { BroadcastMessageFormat, BroadcastMessageInfoFormatWithMetaData, BroadcastReceiverContext } from 'src/states/BroadcastReceiverState';
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { GlassButton } from './GlassButtons';
import { Localization, SessionContext } from '@viamap/viamap2-common';
import { BiCheckCircle } from 'react-icons/bi';
import { ConfirmationDialog } from './ConfirmationDialog';

export function BroadcastReceiverDebug(props: { window: ManagedWindow, onClose: () => void }) {
   const { state: _broadcastReceiverState } = useContext(BroadcastReceiverContext);
   const { state: _sessionState } = useContext(SessionContext);
   const [apiStates, setApiStates] = useState<{ [key: string]: boolean }>({});
   const [activeTab, setActiveTab] = useState<'api' | 'text'>('api');
   const [textBroadcast, setTextBroadcast] = useState({
      title: '',
      message: '',
      type: 'All' as 'All' | 'Org' | 'User',
      SendTo: ''
   });
   const [showConfirmDialog, setShowConfirmDialog] = useState(false);
   const [showSuccessMessage, setShowSuccessMessage] = useState(false);
   const [errorMessage, setErrorMessage] = useState('');

   const apis = [
      { text: 'Address API', feature: ['POI', 'BasicMap'], url: 'https://api.dataforsyningen.dk/rest/gsearch/v2.0/adresse', domain: 'Dataforsyningen' },
      { text: 'Login API', feature: 'POI', url: 'https://api.example.com/auth/login', domain: 'Login API' },
      { text: 'Data Processing API', feature: 'POI', url: 'https://api.example.com/data/process', domain: 'Data Processing API' },
      { text: 'EV Charger API', feature: 'POIEVCharger', url: 'https://api.example.com/ev/chargers', domain: 'EV Charger API' },
      { text: 'Search API', feature: 'SearchBar', url: 'https://api.example.com/search', domain: 'Search API' },
      { text: 'Map API', feature: 'BasicMap', url: 'https://api.example.com/map', domain: 'Map API' }
   ];

   const handleCheckboxChange = (url: string, checked: boolean) => {
      setApiStates(prev => ({
         ...prev,
         [url]: checked
      }));
   };

   const selectAll = () => {
      const allSelected = apis.reduce((acc, api) => {
         acc[api.url] = true;
         return acc;
      }, {} as { [key: string]: boolean });
      setApiStates(allSelected);
   };

   const deselectAll = () => {
      const allDeselected = apis.reduce((acc, api) => {
         acc[api.url] = false;
         return acc;
      }, {} as { [key: string]: boolean });
      setApiStates(allDeselected);
   };

   const toggleAll = () => {
      const allSelected = apis.every(api => apiStates[api.url]);
      if (allSelected) {
         deselectAll();
      } else {
         selectAll();
      }
   };

   const sendBroadcast = () => {
      const failedApis = apis.filter(api => !apiStates[api.url]);

      const message: BroadcastMessageFormat = {
         status: failedApis.length > 0 ? "error" : "success",
         affectedItems: failedApis.map(api => ({
            domain: api.domain,
            url: api.url,
            statusmessage: {
               da: `${api.text} er nede`,
               en: `${api.text} is down`
            },
            licenseFeature: api.feature
         }))
      };

      MitWebsocket.send({
         type: "sendToChannel",
         action: { channel: "broadcast", data: message }
      });

      // Show success message
      setShowSuccessMessage(true);
   };

   const sendTextBroadcast = () => {
      // Clear any previous error message
      setErrorMessage('');

      if (!textBroadcast.title.trim() || !textBroadcast.message.trim()) {
         setErrorMessage(Localization.getText("Please fill in both title and message"));
         return;
      }

      if ((textBroadcast.type === 'Org' || textBroadcast.type === 'User') && !textBroadcast.SendTo.trim()) {
         setErrorMessage(Localization.getText("Please specify the organization or user name"));
         return;
      }

      if (textBroadcast.type === 'All') {
         setShowConfirmDialog(true);
         return;
      }

      actuallySendMessage();
   };

   const actuallySendMessage = () => {
      const message: BroadcastMessageInfoFormatWithMetaData = {
         header: textBroadcast.title,
         message: textBroadcast.message,
         type: textBroadcast.type,
         sendTo: textBroadcast.SendTo
      };

      MitWebsocket.send({
         type: "sendToChannel",
         action: { channel: "broadcastCustom", data: message }
      });

      // Show success message
      setShowSuccessMessage(true);
      setShowConfirmDialog(false);
   };

   // debugState removed (unused)

   // Auto-hide success message after 3 seconds
   useEffect(() => {
      if (showSuccessMessage) {
         const timer = setTimeout(() => {
            setShowSuccessMessage(false);
         }, 3000);
         return () => clearTimeout(timer);
      }
   }, [showSuccessMessage]);

   // Clear error message when user starts typing
   const clearError = () => {
      if (errorMessage) {
         setErrorMessage('');
      }
   };

   // Check if text broadcast form is valid
   const isTextBroadcastValid = () => {
      if (!textBroadcast.title.trim() || !textBroadcast.message.trim()) {
         return false;
      }
      if ((textBroadcast.type === 'Org' || textBroadcast.type === 'User') && !textBroadcast.SendTo.trim()) {
         return false;
      }
      return true;
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("Broadcast Debug Simulator")}
         window={props.window}
         onClose={props.onClose}
         PosDefault={{ left: "50%", top: "20%", width: "50%", height: "70%", transform: "translate(-50%, 0)" }}
      >
         <ADModalBody>
            <div className="broadcast-debug-content">
               {/* Tab Navigation */}
               <div className="broadcast-debug-tabs">
                  <button
                     onClick={() => setActiveTab('api')}
                     className={`broadcast-debug-tab ${activeTab === 'api' ? 'active' : ''}`}
                  >
                     {Localization.getText("API Simulation")}
                  </button>
                  <button
                     onClick={() => setActiveTab('text')}
                     className={`broadcast-debug-tab ${activeTab === 'text' ? 'active' : ''}`}
                  >
                     {Localization.getText("Text Broadcast")}
                  </button>
               </div>

               {/* Tab Content */}
               {activeTab === 'api' && (
                  <>
                     <h4 className="broadcast-debug-section-title">{Localization.getText("API Status Simulation")}</h4>
                     <p className="broadcast-debug-description">
                        {Localization.getText("Check the APIs that should succeed. Unchecked APIs will be marked as failed.")}
                     </p>

                     <div className="broadcast-debug-controls">
                        <GlassButton onClick={toggleAll} className="broadcast-debug-toggle-btn">
                           {Localization.getText("Toggle All")}
                        </GlassButton>
                     </div>

                     <div className="broadcast-debug-api-list">
                        {apis.map((api) => (
                           <div key={api.url} className="broadcast-debug-api-item">
                              <input
                                 type="checkbox"
                                 checked={apiStates[api.url] || false}
                                 onChange={(e) => handleCheckboxChange(api.url, e.target.checked)}
                                 className="broadcast-debug-api-checkbox"
                              />
                              <div className="broadcast-debug-api-content">
                                 <div className="broadcast-debug-api-name">
                                    {api.text}
                                 </div>
                                 <div className="broadcast-debug-api-domain">
                                    {api.domain}
                                 </div>
                                 <div className="broadcast-debug-api-url">
                                    {api.url}
                                 </div>
                                 <div className="broadcast-debug-api-feature">
                                    {Localization.getText("Feature")}: {Array.isArray(api.feature) ? api.feature.join(', ') : api.feature}
                                 </div>
                              </div>
                              <div className={`broadcast-debug-api-status ${apiStates[api.url] ? 'success' : 'fail'}`}>
                                 {apiStates[api.url] ? Localization.getText('SUCCESS') : Localization.getText('FAIL')}
                              </div>
                           </div>
                        ))}
                     </div>
                  </>
               )}

               {activeTab === 'text' && (
                  <>
                     <h4 className="broadcast-debug-section-title">{Localization.getText("Custom Text Broadcast")}</h4>
                     <p className="broadcast-debug-description">
                        {Localization.getText("Send a custom message broadcast to all users.")}
                     </p>

                     {errorMessage && (
                        <div style={{
                           color: '#f44336',
                           backgroundColor: '#ffebee',
                           border: '1px solid #f44336',
                           borderRadius: '4px',
                           padding: '10px',
                           marginBottom: '15px',
                           fontSize: '14px',
                           fontWeight: '500'
                        }}>
                           {errorMessage}
                        </div>
                     )}

                     <div className="broadcast-debug-form">
                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Message Receiver")}
                           </label>
                           <select
                              value={textBroadcast.type}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, type: e.target.value as any, SendTo: '' }));
                              }}
                              className="broadcast-debug-form-select"
                           >
                              <option value="All">{Localization.getText("All")}</option>
                              <option value="Org">{Localization.getText("Organization")}</option>
                              <option value="User">{Localization.getText("User")}</option>
                           </select>
                        </div>

                        {/* Conditional SendTo field - only show for Org or User */}
                        {(textBroadcast.type === 'Org' || textBroadcast.type === 'User') && (
                           <div className="broadcast-debug-form-group">
                              <label className="broadcast-debug-form-label">
                                 {textBroadcast.type === 'Org' ? Localization.getText("Organization (domain, i.e. viamap.net)") : Localization.getText("User (email, i.e. user@viamap.net)")} *
                              </label>
                              <input
                                 type="text"
                                 value={textBroadcast.SendTo}
                                 onChange={(e) => {
                                    clearError();
                                    setTextBroadcast(prev => ({ ...prev, SendTo: e.target.value }));
                                 }}
                                 placeholder={textBroadcast.type === 'Org' ? Localization.getText("Enter organization name...") : Localization.getText("Enter user name...")}
                                 className="broadcast-debug-form-input"
                              />
                           </div>
                        )}

                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Title")} *
                           </label>
                           <input
                              type="text"
                              value={textBroadcast.title}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, title: e.target.value }));
                              }}
                              placeholder={Localization.getText("Enter broadcast title...")}
                              className="broadcast-debug-form-input"
                           />
                        </div>

                        <div className="broadcast-debug-form-group">
                           <label className="broadcast-debug-form-label">
                              {Localization.getText("Message")} *
                           </label>
                           <textarea
                              value={textBroadcast.message}
                              onChange={(e) => {
                                 clearError();
                                 setTextBroadcast(prev => ({ ...prev, message: e.target.value }));
                              }}
                              placeholder={Localization.getText("Enter broadcast message...")}
                              rows={4}
                              className="broadcast-debug-form-textarea"
                           />
                        </div>
                     </div>
                  </>
               )}
            </div>
         </ADModalBody>
         <div style={{ height: '10px' }}></div>
         <ADModalFooter>
            {showSuccessMessage && (
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  color: '#4CAF50',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: showSuccessMessage ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
               }}>
                  <BiCheckCircle size={18} />
                  <span>{Localization.getText("Message sent successfully!")}</span>
               </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
               {activeTab === 'api' ? (
                  <GlassButton onClick={sendBroadcast} className="broadcast-debug-send-btn">
                     {Localization.getText("Send Broadcast")}
                  </GlassButton>
               ) : (
                  <GlassButton title={"Send"} onClick={sendTextBroadcast} className="broadcast-debug-send-btn" disabled={!isTextBroadcastValid()}>
                     {Localization.getText("Send Text Broadcast")}
                  </GlassButton>
               )}
            </div>
         </ADModalFooter>

         <ConfirmationDialog
            showWindow={showConfirmDialog ? 1 : 0}
            header="Confirm Broadcast to All Users"
            onSubmit={() => {
               actuallySendMessage();
               setShowConfirmDialog(false);
            }}
            onCancel={() => setShowConfirmDialog(false)}
         >
            <div>
               <div>
                  {Localization.getText("You are about to send a message to ALL users. This is a significant action that will be seen by everyone.")}
               </div>
               <div>
                  <p></p>
               </div>
               <div>
                  {Localization.getText("Title")}: {textBroadcast.title}
               </div>
               <div>
                  {Localization.getText("Message")}: {textBroadcast.message}
               </div>
               <div>
                  <p></p>
               </div>
               <div>
                  {Localization.getText("Are you sure you want to continue?")}
               </div>
            </div>
         </ConfirmationDialog>
      </AdvancedDragModal>
   );
}